import { integrationsApi } from 'api/integrations/integrations.api';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import { getAppAccounts } from 'redux/selectors/apps';
import type { Thunk } from 'types/redux';
import debug from 'utils/debug';
import OAuthPopup from 'utils/oauth-popup-helper';
import {
  getAreAzureADIntegrationEnabled,
  getArePremiumIntegrationsEnabled,
  getHasNoProjectLimit,
  getNumberOfAvailableProjects,
} from '../selectors/accounts';
import { fetchApps, openAppAccountEditor, refreshAppEditorAccounts } from './apps.actions';
import {
  getAppsEntities,
  getSelectedProjectIdsForImport,
  getWorkspaceProjectIds,
  getWorkspaceProjects,
} from './integrations.selector';
import {
  batchSelectionActions,
  fetchedImportStatus,
  fetchedIntegrationsAction,
  fetchedProjectsForWorkspace,
  selectImportAccountId,
  selectIntegrationWorkspace,
  setImportingFrom,
  submittedImportData,
} from './integrations.types';
import { showSubscriptionNoticeModal } from './subscriptions.actions';

export const { withXhrStatusReducer, wrapSaving, wrapFetching } =
  createXhrStatusModule('integrations');

export function fetchWorkspaces(accountId: number): Thunk {
  return dispatch => {
    return dispatch(wrapFetching(integrationsApi.getWorkspaces(accountId))).then(res => {
      if (res) {
        dispatch(fetchedIntegrationsAction(res));
      } else {
        dispatch(fetchedIntegrationsAction([]));
      }
    });
  };
}

export function fetchProjectsForWorkspace({
  accountId,
  workspaceId,
}: {
  accountId: number;
  workspaceId: number | string;
}): Thunk<Promise<void>> {
  return dispatch => {
    return dispatch(
      wrapFetching(integrationsApi.getProjectsForWorkspace({ accountId, workspaceId }))
    ).then(res => {
      dispatch(fetchedProjectsForWorkspace(res));
    });
  };
}

export function importDataForWorkspace({
  accountId,
  workspaceId,
  projectIds,
}: {
  accountId: number;
  workspaceId: number | string;
  projectIds: Array<number | string>;
}): Thunk {
  return dispatch => {
    dispatch(setImportingFrom({ accountId }));
    return dispatch(
      wrapSaving(
        integrationsApi.startImport({
          accountId,
          workspaceId,
          projectIds,
        })
      )
    ).then(() => {
      dispatch(submittedImportData({ accountId }));
    });
  };
}

export function importAllDataForWorkspace({ accountId }: { accountId: number }): Thunk {
  return async dispatch => {
    dispatch(setImportingFrom({ accountId }));
    const workspaces = await integrationsApi.getWorkspaces(accountId);
    if (workspaces.length > 0) {
      // we're not allowing the user to choose which workspace, so just take the first one
      const workspaceId = workspaces[0].id;
      dispatch(selectIntegrationWorkspace({ id: workspaceId }));
      return dispatch(
        wrapSaving(
          integrationsApi.startImport({
            accountId,
            workspaceId,
            projectIds: ['all'],
          })
        )
      ).then(() => {
        dispatch(submittedImportData({ accountId }));
      });
    }
  };
}

export function getImportStatus(accountId: number): Thunk {
  return dispatch => {
    let handler: number;
    const getStatus = () =>
      dispatch(wrapFetching(integrationsApi.getImportStatus(accountId))).then(status => {
        dispatch(fetchedImportStatus({ status, accountId }));

        if (Array.isArray(status)) {
          const lastItem = status[status.length - 1];
          // Import complete, stop polling
          if (lastItem?.status === 'finished') {
            if (handler) {
              window.clearTimeout(handler);
            }
            return;
          }
        }
        // Import not done, keep polling
        handler = window.setTimeout(getStatus, 3000);
      });
    getStatus();
  };
}

export function smartlySelectAllProjects(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const hasNoProjectLimit = getHasNoProjectLimit(state);
    const availableProjects = !hasNoProjectLimit ? getNumberOfAvailableProjects(state) : 999999;
    const workspaceProjects = getWorkspaceProjects(state);

    const selectedProjectIds = getSelectedProjectIdsForImport(state);
    const workspaceProjectIds = getWorkspaceProjectIds(state);
    if (selectedProjectIds.size > 0) {
      const filterIntersectingProjects = workspaceProjectIds.filter(
        projectId => !selectedProjectIds.has(Number(projectId.toString()))
      );
      const projectIdsToSelect = filterIntersectingProjects.slice(
        0,
        availableProjects - selectedProjectIds.size
      );

      dispatch(
        batchSelectionActions.setSelection({
          selectionKey: 'projectIds',
          value: [...selectedProjectIds, ...projectIdsToSelect],
        })
      );
      return;
    }
    if (availableProjects === selectedProjectIds.size) {
      dispatch(batchSelectionActions.clearSelection());
      return;
    }

    if (workspaceProjects.length < availableProjects) {
      dispatch(
        batchSelectionActions.setSelection({
          selectionKey: 'projectIds',
          value: workspaceProjectIds,
        })
      );
      return;
    }

    const projectIdsToSelect = workspaceProjects
      .slice(0, availableProjects)
      .map(project => project.item.value);

    dispatch(
      batchSelectionActions.setSelection({
        selectionKey: 'projectIds',
        value: projectIdsToSelect,
      })
    );
  };
}

export function enableIntegration(
  appId: number,
  options: {
    autoImportOnConnect?: boolean;
    showImportWizardOnConnect?: boolean;
    showSettingsOnConnect?: boolean;
  }
): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const appEntities = getAppsEntities(state);
    const canUsePremiumIntegrations = getArePremiumIntegrationsEnabled(state);
    const canUseAzureADIntegration = getAreAzureADIntegrationEnabled(state);
    const { id, premium, unlimited_plus, authorize_url, authorize_full_url } = appEntities[appId];

    if (!canUseAzureADIntegration && unlimited_plus) {
      return dispatch(showSubscriptionNoticeModal('azureADIntegrationRequiresUpgrade'));
    }

    if (!canUsePremiumIntegrations && premium) {
      return dispatch(showSubscriptionNoticeModal('premiumIntegrationsRequiresUpgrade'));
    }

    try {
      const oAuthConnect = await OAuthPopup.connect(id, authorize_full_url || authorize_url);
      if (!oAuthConnect) return;

      await dispatch(fetchApps());

      const newAccounts = await dispatch(refreshAppEditorAccounts(appId));
      if (!newAccounts?.length) return;

      // Try to open account editor for the newly added account, if possible
      const [newAccount] = newAccounts;
      if (options.autoImportOnConnect) {
        dispatch(importAllDataForWorkspace({ accountId: newAccount.id }));
      } else if (options.showImportWizardOnConnect) {
        dispatch(selectImportAccountId({ id: newAccount.id, name: newAccount.name }));
      } else if (options.showSettingsOnConnect) {
        dispatch(openAppAccountEditor(newAccount.id));
      }
    } catch (error) {
      debug.error('OAuthPopup error:', error);
    }
  };
}

// Import from an already connected integration without choosing account
export function quickImportFromIntegration(
  appId: number,
  options: { autoImportOnConnect: boolean }
): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    dispatch(refreshAppEditorAccounts(appId)).then(accounts => {
      const state = getState();
      const appAccounts = getAppAccounts(state);
      const appEntities = getAppsEntities(state);
      // if accounts is the same as what's in state, refreshAppEditorAccounts returns an empty array
      // so look it it up in state instead
      const connectionToTry = accounts[0] || appAccounts[appId][0];
      const lookupApp = appEntities[appId];

      if (connectionToTry && options.autoImportOnConnect) {
        dispatch(importAllDataForWorkspace({ accountId: connectionToTry.id }));
        return;
      }

      if (connectionToTry) {
        dispatch(selectImportAccountId({ id: connectionToTry.id, name: lookupApp.id }));
        return;
      }
    });
  };
}
